import { createFileRoute, Link } from "@tanstack/react-router";
import { useSuspenseQuery, queryOptions } from "@tanstack/react-query";
import { Suspense } from "react";
import { SiteHeader } from "@/components/site/SiteHeader";
import { SiteFooter } from "@/components/site/SiteFooter";
import { NewsletterCTA } from "@/components/site/NewsletterCTA";
import { CiteThis } from "@/components/site/CiteThis";
import { listAlgorithmUpdates, type AlgorithmUpdate } from "@/lib/backlink.functions";
import { SITE, formatDate } from "@/lib/site";

const updatesQuery = queryOptions({
  queryKey: ["algorithm-updates"],
  queryFn: () => listAlgorithmUpdates(),
});

export const Route = createFileRoute("/updates/")({
  loader: ({ context }) => context.queryClient.ensureQueryData(updatesQuery),
  head: () => {
    const url = `${SITE.url}/updates`;
    return {
      meta: [
        { title: `Local Search Algorithm Updates Tracker | ${SITE.name}` },
        {
          name: "description",
          content:
            "Confirmed and suspected Google local-search algorithm updates — with severity scores, affected surfaces, and start/end dates. Updated by the Map Observer newsroom.",
        },
        { property: "og:title", content: `Local Search Algorithm Update Tracker | ${SITE.name}` },
        { property: "og:description", content: "Confirmed and suspected Google local algorithm updates, scored and dated." },
        { property: "og:url", content: url },
        { property: "og:type", content: "website" },
      ],
      links: [{ rel: "canonical", href: url }],
      scripts: [
        {
          type: "application/ld+json",
          children: JSON.stringify({
            "@context": "https://schema.org",
            "@type": "CollectionPage",
            name: "Local Search Algorithm Updates",
            url,
            isPartOf: { "@type": "WebSite", name: SITE.name, url: SITE.url },
          }),
        },
      ],
    };
  },
  component: UpdatesPage,
});

function severityClass(s: number): string {
  if (s >= 8) return "bg-red-600 text-white";
  if (s >= 6) return "bg-orange-500 text-white";
  if (s >= 4) return "bg-yellow-400 text-foreground";
  return "bg-green-500 text-white";
}

function UpdatesPage() {
  return (
    <div className="min-h-screen bg-background">
      <SiteHeader />
      <main>
        <Suspense fallback={<div className="py-24 text-center text-muted-foreground">Loading…</div>}>
          <UpdatesBody />
        </Suspense>
      </main>
      <SiteFooter />
    </div>
  );
}

function UpdatesBody() {
  const { data } = useSuspenseQuery(updatesQuery);
  const updates = data as AlgorithmUpdate[];

  return (
    <div className="mx-auto max-w-5xl px-5 sm:px-8 py-16">
      <nav aria-label="Breadcrumb" className="text-xs uppercase tracking-wider text-muted-foreground">
        <ol className="flex items-center gap-2 flex-wrap">
          <li><Link to="/" className="hover:text-accent">Home</Link></li>
          <li aria-hidden>/</li>
          <li>Algorithm updates</li>
        </ol>
      </nav>
      <header className="mt-6 max-w-2xl">
        <p className="eyebrow">Tracker</p>
        <h1 className="headline-display mt-3 text-5xl">Local search algorithm updates</h1>
        <p className="mt-4 text-lg text-muted-foreground">
          Every confirmed and suspected local-search algorithm shift, with our severity score,
          affected surfaces, and start/end dates. The map-pack equivalent of Mozcast.
        </p>
      </header>

      <div className="mt-8 flex items-center gap-3 border-t border-b border-border py-4">
        <CiteThis title="Local Search Algorithm Updates Tracker" url={`${SITE.url}/updates`} />
        <Link to="/local-search-index" className="text-xs uppercase tracking-wider text-muted-foreground hover:text-accent">
          Live volatility →
        </Link>
      </div>

      {updates.length === 0 ? (
        <p className="mt-16 text-center text-muted-foreground">No updates tracked yet.</p>
      ) : (
        <div className="mt-12 grid gap-6">
          {updates.map((u) => (
            <article key={u.id} className="group rounded-sm border border-border bg-paper-soft transition hover:border-foreground/40 hover:shadow-sm">
              <Link
                to="/updates/$slug"
                params={{ slug: u.slug }}
                className="block p-6"
                aria-label={`${u.name} — severity ${Number(u.severity).toFixed(1)}`}
              >
                <div className="flex items-start justify-between gap-4 flex-wrap">
                  <div>
                    <div className="text-xs uppercase tracking-wider text-muted-foreground flex flex-wrap items-center gap-3">
                      <span className={`px-2 py-0.5 ${u.status === "confirmed" ? "border border-foreground" : "border border-border"} bg-background`}>
                        {u.status}
                      </span>
                      <time dateTime={u.started_at}>{formatDate(u.started_at)}</time>
                      {u.ended_at && <><span aria-hidden>→</span><time dateTime={u.ended_at}>{formatDate(u.ended_at)}</time></>}
                    </div>
                    <h2 className="mt-2 text-xl font-semibold group-hover:text-accent">{u.name}</h2>
                  </div>
                  <div className={`rounded-sm px-3 py-2 text-center ${severityClass(Number(u.severity))}`}>
                    <p className="text-[10px] uppercase tracking-wider opacity-80">Severity</p>
                    <p className="text-2xl font-bold leading-none">{Number(u.severity).toFixed(1)}</p>
                  </div>
                </div>
                <p className="mt-3 text-base text-foreground/85 leading-relaxed">{u.summary}</p>
                <div className="mt-4 flex flex-wrap items-center gap-3 text-xs text-muted-foreground">
                  <span className="eyebrow">Surfaces</span>
                  {(u.affected_surfaces as string[]).map((s) => (
                    <span key={s} className="rounded-sm border border-border bg-background px-2 py-0.5">{s}</span>
                  ))}
                  <span className="grow" />
                  <span className="underline group-hover:text-accent">Read update →</span>
                </div>
              </Link>
            </article>
          ))}
        </div>
      )}

      <NewsletterCTA />
    </div>
  );
}
